import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import { Link, useLocation } from 'react-router-dom';
import logohdf from '../images/logohdf.jpg';
import { brand } from '../theme';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { text: 'Home', path: '/' },
    { text: 'Hauling', path: '/services' },
    { text: 'Rentals', path: '/rentals' },
    { text: 'Privacy', path: '/privacy' },
  ];

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: `linear-gradient(180deg, ${brand.ink} 0%, #17171b 100%)`,
          borderBottom: `3px solid ${brand.red}`,
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          {/* Logo and Title */}
          <Box
            component={Link}
            to="/"
            sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, textDecoration: 'none' }}
          >
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '8px',
                overflow: 'hidden',
                mr: 2,
                border: `2px solid ${brand.red}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
              }}
            >
              <img
                src={logohdf}
                alt="HDF Hauling Logo"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </Box>
            <Box>
              <Typography
                variant="h5"
                component="div"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  lineHeight: 1,
                  letterSpacing: '0.5px',
                }}
              >
                HDF HAULING
              </Typography>
              <Typography
                sx={{
                  color: brand.redBright,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  letterSpacing: '3px',
                  textTransform: 'uppercase',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Hauling &amp; Trailer Rentals
              </Typography>
            </Box>
          </Box>

          {/* Full Menu for Desktop */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            {navItems.map((item) => (
              <Button
                key={item.text}
                component={Link}
                to={item.path}
                sx={{
                  color: isActive(item.path) ? brand.redBright : 'white',
                  mx: 0.5,
                  px: 2,
                  py: 1,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderRadius: 1,
                  position: 'relative',
                  transition: 'all 0.25s ease',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)', color: brand.redBright },
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 4,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: isActive(item.path) ? '60%' : 0,
                    height: '2px',
                    backgroundColor: brand.red,
                    transition: 'width 0.25s ease',
                  },
                  '&:hover:after': { width: '60%' },
                }}
              >
                {item.text}
              </Button>
            ))}

            {/* Primary CTA */}
            <Button
              component={Link}
              to="/#rental-quote"
              startIcon={<RequestQuoteIcon />}
              sx={{
                ml: 2,
                px: 2.5,
                py: 1.1,
                color: 'white',
                background: `linear-gradient(45deg, ${brand.redDark}, ${brand.red})`,
                boxShadow: '0 4px 14px rgba(225,20,20,0.4)',
                '&:hover': {
                  background: `linear-gradient(45deg, ${brand.red}, ${brand.redBright})`,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Get a Quote
            </Button>
          </Box>

          {/* Hamburger Menu Icon for Mobile */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <IconButton
              color="inherit"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
            >
              <MenuIcon sx={{ fontSize: '1.8rem', color: 'white' }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer for Mobile Menu */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            background: `linear-gradient(180deg, ${brand.ink} 0%, #17171b 100%)`,
            color: 'white',
            width: 280,
            borderLeft: `3px solid ${brand.red}`,
          },
        }}
      >
        <Box sx={{ width: 280, pt: 2 }} role="presentation">
          {/* Mobile Menu Header */}
          <Box sx={{ px: 3, pb: 2, borderBottom: `1px solid ${brand.line}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '6px',
                  overflow: 'hidden',
                  mr: 2,
                  border: `1px solid ${brand.red}`,
                }}
              >
                <img
                  src={logohdf}
                  alt="HDF Hauling Logo"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                HDF HAULING
              </Typography>
            </Box>
          </Box>

          <List sx={{ pt: 2 }} onClick={handleDrawerToggle}>
            {navItems.map((item) => (
              <ListItem
                button
                key={item.text}
                component={Link as React.ElementType}
                to={item.path}
                sx={{
                  py: 2,
                  px: 3,
                  color: isActive(item.path) ? brand.redBright : 'white',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontFamily: '"Oswald", sans-serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                />
              </ListItem>
            ))}
          </List>

          {/* Mobile CTA */}
          <Box sx={{ px: 3, pt: 2 }}>
            <Button
              fullWidth
              component={Link}
              to="/#rental-quote"
              onClick={handleDrawerToggle}
              startIcon={<RequestQuoteIcon />}
              sx={{
                py: 1.4,
                color: 'white',
                background: `linear-gradient(45deg, ${brand.redDark}, ${brand.red})`,
                '&:hover': { background: `linear-gradient(45deg, ${brand.red}, ${brand.redBright})` },
              }}
            >
              Get a Quote
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
